import React, { useEffect, useRef, useState } from 'react';

const InvoiceCreateFilters = ({
  isOpen,
  onClose,
  filters,
  setFilters,
  clients,
  states,
  onApplyFilters,
  buttonRef
}) => {
  const dropdownRef = useRef(null);
  const [position, setPosition] = useState({ top: 'full', left: 'left' });

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') onClose();
    };

    const handleClickOutside = (e) => {
      if (
        dropdownRef.current && !dropdownRef.current.contains(e.target) &&
        buttonRef?.current && !buttonRef.current.contains(e.target)
      ) {
        onClose();
      }
    };

    if (isOpen) {
      window.addEventListener('keydown', handleEscape);
      document.addEventListener('mousedown', handleClickOutside);
      
      // Calculate position to avoid going off-screen
      if (buttonRef?.current && dropdownRef.current) {
        const buttonRect = buttonRef.current.getBoundingClientRect();
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;
        const dropdownWidth = 384; // w-96 = 24rem = 384px
        const dropdownHeight = 300; // Approximate height
        
        let top = 'full';
        let left = 'left';
        
        // Check if dropdown would go off the right edge
        if (buttonRect.right + dropdownWidth > viewportWidth) {
          left = 'right';
        }
        
        // Check if dropdown would go off the bottom edge
        if (buttonRect.bottom + dropdownHeight > viewportHeight) {
          top = 'top';
        }
        
        setPosition({ top, left });
      }
    }

    return () => {
      window.removeEventListener('keydown', handleEscape);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose, buttonRef]);

  if (!isOpen) return null;

  const getPositionClasses = () => {
    let classes = "absolute w-96 bg-white rounded-lg shadow-lg border border-gray-200 z-50";
    
    // Handle vertical positioning
    if (position.top === 'top') {
      classes += " bottom-full mb-1";
    } else {
      classes += " top-full mt-1";
    }
    
    // Handle horizontal positioning
    if (position.left === 'right') {
      classes += " right-0";
    } else {
      classes += " left-0";
    }
    
    return classes;
  };

  return (
    <div
      ref={dropdownRef}
      className={getPositionClasses()}
      role="dialog"
      aria-labelledby="invoice-create-filter-title"
    >
      <div className="p-4">
        <div className="flex justify-between items-center mb-4">
          <h3 id="invoice-create-filter-title" className="text-lg font-semibold text-gray-900">Filters</h3>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 transition-colors"
            aria-label="Close filters"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Client</label>
            <select
              value={filters.client_name}
              onChange={(e) => setFilters({ ...filters, client_name: e.target.value })}
              className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-500"
              aria-label="Filter by client name"
            >
              <option value="">All Clients</option>
              {clients.map((client) => (
                <option key={client} value={client}>{client}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">State</label>
            <select
              value={filters.state}
              onChange={(e) => setFilters({ ...filters, state: e.target.value })}
              className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-500"
              aria-label="Filter by state"
            >
              <option value="">All States</option>
              {states.map((state) => (
                <option key={state} value={state}>{state}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Amount Status</label>
            <select
              value={filters.amount_status}
              onChange={(e) => setFilters({ ...filters, amount_status: e.target.value })}
              className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-500"
              aria-label="Filter by amount status"
            >
              <option value="">All</option>
              <option value="full_pending">Full Pending</option>
              <option value="partial_pending">Partial Pending</option>
            </select>
          </div>
        </div>

        <div className="mt-4 flex justify-end space-x-2">
          <button
            onClick={onClose}
            className="px-3 py-1.5 text-sm text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={onApplyFilters}
            className="px-3 py-1.5 text-sm bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors"
          >
            Apply
          </button>
        </div>
      </div>
    </div>
  );
};

export default InvoiceCreateFilters;


